import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { motion } from "framer-motion";
import { 
  ArrowLeft, Package, Beaker, Clock, CheckCircle2, 
  Search, Filter, Building2, ArrowUpRight, Mail, Phone, MapPin, RefreshCw, Loader2
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Fetch client contact details from Zoho
  const fetchClientDetails = useCallback(async (clientName) => {
    try {
      setLoadingDetails(true);
      const response = await axios.get(`${API}/zoho/client-requirements/${encodeURIComponent(clientName)}`);
      if (response.data.success && response.data.client) {
        setClientDetails(response.data.client);
      }
    } catch (error) {
      console.error("Failed to fetch client details:", error);
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  const fetchClientData = useCallback(async () => {
    try {
      // Fetch all product folders and filter by client
      const response = await axios.get(`${API}/product-folders`);
      if (response.data.success) {
        const allFolders = response.data.folders || [];
        const clientName = decodeURIComponent(clientId);
        
        // Filter folders for this client (case-insensitive)
        const clientFolders = allFolders.filter(f => 
          f.clientName?.toLowerCase().trim() === clientName.toLowerCase().trim()
        );
        
        // Calculate client stats
        const total = clientFolders.length;
        const completed = clientFolders.filter(f => f.testStatus === 'Complete' || f.testStatus === 'Finish').length;
        const active = clientFolders.filter(f => ['Started', 'End', 'In Progress', 'Under R&D'].includes(f.testStatus)).length;
        
        setClient({
          name: clientName,
          totalJobs: total,
          activeJobs: active,
          completedJobs: completed
        });
        setProducts(clientFolders);
        
        // Also fetch client contact details from Zoho
        fetchClientDetails(clientName);
      }
    } catch (error) {
      console.error("Failed to fetch client data:", error);
      toast.error("Failed to fetch client data");
    } finally {
      setLoading(false);
    }
  }, [clientId, fetchClientDetails]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.sampleNo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || product.testStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'Complete':
      case 'Finish': return 'bg-emerald-100 text-emerald-700';
      case 'Started':
      case 'End':
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      case 'Not Started': return 'bg-slate-100 text-slate-600';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-vcc-teal font-poppins">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" data-testid="client-detail-page">
      {/* Header with Back Button */}
      <motion.div 
        className="flex items-center gap-4 mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Button 
          variant="ghost" 
          onClick={() => navigate('/clients')}
          className="hover:bg-vcc-pink-50"
        >
          <ArrowLeft size={20} />
        </Button>
        
        <div className="flex-1">
          <h1 className="text-3xl font-poppins font-bold text-slate-800">{client?.name}</h1>
          <p className="text-slate-500 font-poppins">Client Details & Products</p>
        </div>
        
        <Button 
          variant="outline"
          onClick={fetchClientData}
          className="border-vcc-teal text-vcc-teal hover:bg-vcc-teal/10"
        >
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </motion.div>

      {/* Client Details Card */}
      <motion.div 
        className="backdrop-blur-xl bg-gradient-to-r from-vcc-teal/5 to-vcc-coral/5 border border-vcc-pink-100 rounded-2xl p-6 mb-6 shadow-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        <div className="flex flex-wrap items-start gap-6">
          {/* Client Avatar & Name */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-vcc-teal to-vcc-coral flex items-center justify-center text-white font-poppins font-bold text-2xl shadow-lg">
              {client?.name?.charAt(0) || 'C'}
            </div>
            <div>
              <h2 className="text-xl font-poppins font-bold text-slate-800">{client?.name}</h2>
              {clientDetails?.companyName && clientDetails.companyName !== client?.name && (
                <p className="text-vcc-teal font-poppins font-medium flex items-center gap-1">
                  <Building2 size={14} />
                  {clientDetails.companyName}
                </p>
              )}
            </div>
          </div>

          {/* Contact Details */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {loadingDetails ? (
              <div className="col-span-full flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-vcc-teal mr-2" />
                <span className="text-sm text-slate-500 font-poppins">Loading contact details...</span>
              </div>
            ) : (
              <>
                {clientDetails?.email && (
                  <div className="flex items-center gap-3 bg-white/60 rounded-xl px-4 py-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Mail size={18} className="text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-slate-400 font-poppins">Email</p>
                      <a href={`mailto:${clientDetails.email}`} className="text-sm font-medium text-slate-700 hover:text-vcc-teal font-poppins truncate block">
                        {clientDetails.email}
                      </a>
                    </div>
                  </div>
                )}
                
                {clientDetails?.phoneNumber && (
                  <div className="flex items-center gap-3 bg-white/60 rounded-xl px-4 py-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Phone size={18} className="text-emerald-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-slate-400 font-poppins">Phone</p>
                      <a href={`tel:${clientDetails.phoneNumber}`} className="text-sm font-medium text-slate-700 hover:text-vcc-teal font-poppins truncate block">
                        {clientDetails.phoneNumber}
                      </a>
                    </div>
                  </div>
                )}
                
                {clientDetails?.location && (
                  <div className="flex items-center gap-3 bg-white/60 rounded-xl px-4 py-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <MapPin size={18} className="text-amber-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-slate-400 font-poppins">Location</p>
                      <p className="text-sm font-medium text-slate-700 font-poppins truncate">
                        {clientDetails.location}
                      </p>
                    </div>
                  </div>
                )}
                
                {!clientDetails?.email && !clientDetails?.phoneNumber && !clientDetails?.location && (
                  <div className="col-span-full text-center py-2">
                    <p className="text-sm text-slate-400 font-poppins italic">No contact details available in Zoho</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Client Stats */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="backdrop-blur-xl bg-white/80 border border-vcc-pink-100 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-vcc-teal/10 flex items-center justify-center">
              <Package className="w-6 h-6 text-vcc-teal" />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-poppins">Total Products</p>
              <p className="text-2xl font-bold text-slate-800">{client?.totalJobs || 0}</p>
            </div>
          </div>
        </div>

        <div className="backdrop-blur-xl bg-white/80 border border-vcc-pink-100 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-poppins">Active</p>
              <p className="text-2xl font-bold text-slate-800">{client?.activeJobs || 0}</p>
            </div>
          </div>
        </div>

        <div className="backdrop-blur-xl bg-white/80 border border-vcc-pink-100 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500 font-poppins">Completed</p>
              <p className="text-2xl font-bold text-slate-800">{client?.completedJobs || 0}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Products Section Header */}
      <motion.div
        className="flex items-center gap-2 mb-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <Beaker className="w-5 h-5 text-vcc-teal" />
        <h3 className="text-lg font-poppins font-semibold text-slate-800">Products List</h3>
        <Badge className="bg-vcc-teal/10 text-vcc-teal font-poppins">{filteredProducts.length} products</Badge>
      </motion.div>

      {/* Filters */}
      <motion.div 
        className="flex flex-wrap gap-4 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/80 border-vcc-pink-100 focus:border-vcc-teal font-poppins"
          />
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 rounded-lg bg-white/80 border border-vcc-pink-100 text-slate-700 font-poppins focus:border-vcc-teal outline-none"
        >
          <option value="all">All Status</option>
          <option value="Not Started">Not Started</option>
          <option value="Started">Started</option>
          <option value="End">End</option>
          <option value="Complete">Complete</option>
          <option value="Finish">Finish</option>
        </select>
      </motion.div>

      {/* Products Table */}
      <motion.div 
        className="backdrop-blur-xl bg-white/80 border border-vcc-pink-100 rounded-2xl shadow-lg overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-vcc-cream-50 border-b border-vcc-pink-100">
              <tr>
                <th className="text-left py-4 px-6 font-poppins font-semibold text-slate-700">Product Name</th>
                <th className="text-left py-4 px-6 font-poppins font-semibold text-slate-700">Sample No</th>
                <th className="text-left py-4 px-6 font-poppins font-semibold text-slate-700">SKU</th>
                <th className="text-left py-4 px-6 font-poppins font-semibold text-slate-700">Test Status</th>
                <th className="text-left py-4 px-6 font-poppins font-semibold text-slate-700">Iterations</th>
                <th className="text-left py-4 px-6 font-poppins font-semibold text-slate-700"></th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500">
                    <Beaker className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-poppins">No products found</p>
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product, index) => (
                  <motion.tr
                    key={product.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.03 }}
                    className="border-b border-vcc-pink-50 hover:bg-vcc-pink-50/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/products/${product.id}`)}
                  >
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-vcc-teal/10 flex items-center justify-center">
                          <Beaker className="w-5 h-5 text-vcc-teal" />
                        </div>
                        <span className="font-poppins font-medium text-slate-800">{product.productName}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-slate-600 font-poppins">{product.sampleNo}</td>
                    <td className="py-4 px-6 text-slate-600 font-poppins">{product.sku || '-'}</td>
                    <td className="py-4 px-6">
                      <Badge className={`${getStatusColor(product.testStatus)} font-poppins`}>
                        {product.testStatus || 'Not Started'}
                      </Badge>
                    </td>
                    <td className="py-4 px-6 text-slate-600 font-poppins">
                      {product.iterations?.length || 0}
                    </td>
                    <td className="py-4 px-6">
                      <ArrowUpRight className="w-5 h-5 text-slate-400" />
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
